'use client';

// Seleção com busca (combobox): substitui o <select> quando há muitas opções
// (ex.: dezenas de modelos de IA). Filtra por label/hint conforme o usuário digita.

import { useEffect, useRef, useState } from 'react';

export interface SearchableOption {
  value: string;
  label: string;
  hint?: string; // ex.: provider do modelo
}

export interface SearchableSelectProps {
  options: SearchableOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Rótulo de uma opção "vazia" no topo (ex.: "— sem fallback —"). */
  emptyLabel?: string;
  emptyValue?: string;
}

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Buscar…',
  disabled,
  emptyLabel,
  emptyValue = 'none',
}: SearchableSelectProps) {
  const [aberto, setAberto] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false); };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const selecionado = value === emptyValue
    ? (emptyLabel ?? '')
    : options.find((o) => o.value === value)?.label ?? value;

  const filtro = q.trim().toLowerCase();
  const filtradas = filtro
    ? options.filter((o) => o.label.toLowerCase().includes(filtro) || (o.hint ?? '').toLowerCase().includes(filtro))
    : options;

  const escolher = (v: string) => { onChange(v); setAberto(false); setQ(''); };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setAberto((a) => !a)}
        className="flex w-full items-center justify-between gap-1 rounded-md border px-2.5 py-1.5 text-left text-[12.5px] disabled:opacity-50"
        style={{ borderColor: 'var(--border)', background: 'var(--bg-card)', color: 'var(--fg)' }}
      >
        <span className="truncate">{selecionado || placeholder}</span>
        <i className="pi pi-chevron-down text-[10px]" style={{ color: 'var(--fg-muted)' }} aria-hidden="true" />
      </button>

      {aberto && (
        <div
          className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border shadow-lg"
          style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}
        >
          <div className="sticky top-0 p-1.5" style={{ background: 'var(--bg-card)' }}>
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder}
              className="w-full rounded border px-2 py-1 text-[12px]"
              style={{ borderColor: 'var(--border)', background: 'var(--bg-page)', color: 'var(--fg)' }}
            />
          </div>
          {emptyLabel && (
            <button type="button" onClick={() => escolher(emptyValue)}
              className="block w-full px-2.5 py-1.5 text-left text-[12px] hover:bg-[var(--bg-hover)]"
              style={{ color: 'var(--fg-muted)' }}>
              {emptyLabel}
            </button>
          )}
          {filtradas.length === 0 ? (
            <div className="px-2.5 py-2 text-[12px]" style={{ color: 'var(--fg-muted)' }}>Nenhum modelo encontrado.</div>
          ) : (
            filtradas.map((o) => (
              <button key={o.value} type="button" onClick={() => escolher(o.value)}
                className="block w-full px-2.5 py-1.5 text-left text-[12px] hover:bg-[var(--bg-hover)]"
                style={{ color: 'var(--fg)', background: o.value === value ? 'var(--ai-50)' : undefined }}>
                {o.label}
                {o.hint && <span style={{ color: 'var(--fg-muted)' }}> · {o.hint}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
